import Image from 'next/image';
import type { ComponentType, SVGProps } from 'react';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';

import { useB2bTranslation } from './kit';

// The file names are historical; each comment says which company it is.
import GeLogo from '~/svg/Barwenock.svg'; // GE
import TakashimayaLogo from '~/svg/Bengs.svg'; // Takashimaya
import SephoraLogo from '~/svg/Supabase.svg'; // Sephora
import PwcLogo from '~/svg/Tailwind.svg'; // PwC
import RazorpayLogo from '~/svg/Typescript.svg'; // Razorpay

type Logo =
  | {
      name: string;
      Svg: ComponentType<SVGProps<SVGSVGElement>>;
      className: string;
      /** Monograms with white detail inside would fill solid as a silhouette. */
      tone?: 'grayscale';
    }
  | {
      name: string;
      src: string;
      width: number;
      height: number;
      className: string;
    };

const DIR = '/images/b2b/customers/';

// Sized by eye so wordmarks and monograms read the same size. Logos are drawn
// as silhouettes (brightness 0) so they share one tone in both themes; GE's
// monogram is greyed instead, or its white lettering would fill in.
const LOGOS: Logo[] = [
  { name: 'GE', Svg: GeLogo, className: 'h-10 w-10', tone: 'grayscale' },
  {
    name: 'Honeywell',
    src: `${DIR}honeywell.svg`,
    width: 153,
    height: 28,
    className: 'h-[22px] w-auto',
  },
  {
    name: 'Deloitte',
    src: `${DIR}deloitte.png`,
    width: 2560,
    height: 557,
    className: 'h-[22px] w-auto',
  },
  { name: 'PwC', Svg: PwcLogo, className: 'h-9 w-[47px]' },
  { name: 'Sephora', Svg: SephoraLogo, className: 'h-[15px] w-[116px]' },
  {
    name: 'Zoho',
    src: `${DIR}zoho.png`,
    width: 512,
    height: 218,
    className: 'h-9 w-auto',
  },
  { name: 'Razorpay', Svg: RazorpayLogo, className: 'h-6 w-[114px]' },
  {
    name: 'Zepto',
    src: `${DIR}zepto.svg`,
    width: 90,
    height: 30,
    className: 'h-7 w-auto',
  },
  {
    name: 'Takashimaya',
    Svg: TakashimayaLogo,
    className: 'h-[22px] w-[124px]',
  },
  {
    name: 'UC San Diego',
    src: `${DIR}uc-san-diego.png`,
    width: 510,
    height: 102,
    className: 'h-6 w-auto',
  },
  {
    name: 'Chicago Public Schools',
    src: `${DIR}chicago-public-schools.svg`,
    width: 184,
    height: 90,
    className: 'h-10 w-auto',
  },
  {
    name: 'Tecnológico de Monterrey',
    src: `${DIR}tecnologico-de-monterrey.png`,
    width: 380,
    height: 102,
    className: 'h-8 w-auto',
  },
];

const silhouette =
  'flex-none brightness-0 opacity-50 transition duration-300 hover:opacity-80 dark:invert dark:opacity-60 dark:hover:opacity-90';
const grayscale =
  'flex-none grayscale opacity-60 transition duration-300 hover:opacity-90 dark:invert dark:opacity-60 dark:hover:opacity-90';

export default function TeamsLogos() {
  const { t } = useB2bTranslation();

  return (
    <section className='mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-20 lg:px-8'>
      <p className={clsxm('text-center text-sm font-medium', tone.quiet)}>
        {t('Trusted by teams at companies and universities like')}
      </p>
      <ul className='mt-10 grid grid-cols-2 items-center gap-x-8 gap-y-10 sm:grid-cols-3 lg:grid-cols-6 lg:gap-y-12'>
        {LOGOS.map((logo) => (
          <li key={logo.name} className='flex h-10 items-center justify-center'>
            {'Svg' in logo ? (
              <logo.Svg
                role='img'
                aria-label={logo.name}
                className={clsxm(
                  logo.tone === 'grayscale' ? grayscale : silhouette,
                  logo.className,
                )}
              />
            ) : (
              <Image
                src={logo.src}
                alt={logo.name}
                width={logo.width}
                height={logo.height}
                unoptimized={logo.src.endsWith('.svg')}
                className={clsxm(silhouette, logo.className)}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
